import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { cn } from "@/lib/utils";

type SliderProps = React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  /**
   * `default` : slider shadcn standard (boost, enregistreur…).
   * `player` : barre de progression du lecteur, pilotée par des variables CSS
   * (`--player-progress-track-height`, `--player-progress-thumb-size`,
   * `--player-progress-thumb-opacity`) réglables en administration.
   */
  variant?: "default" | "player";
};

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(({
  className,
  variant = "default",
  ...props
}, ref) => {
  const isPlayer = variant === "player";

  return (
    <SliderPrimitive.Root ref={ref} className={cn("relative flex w-full touch-none select-none items-center data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col", className)} {...props}>
      <SliderPrimitive.Track
        className={cn(
          "relative w-full grow overflow-hidden data-[orientation=vertical]:h-full data-[orientation=vertical]:w-2",
          isPlayer
            ? "rounded-none bg-player-foreground/30"
            : "h-2 rounded-full bg-secondary",
        )}
        style={isPlayer ? { height: "var(--player-progress-track-height, 1px)" } : undefined}
      >
        <SliderPrimitive.Range
          className={cn("absolute data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-full", isPlayer ? "rounded-none bg-player-foreground" : "h-full bg-primary")}
          style={isPlayer ? { height: "var(--player-progress-track-height, 1px)" } : undefined}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        // Pouce du lecteur : une opacité nulle ne suffit pas à le neutraliser,
        // on coupe aussi les évènements pointeur (le glissement reste géré par la racine).
        className={cn(
          "block",
          isPlayer
            ? "overflow-hidden rounded-full border-0 bg-player-foreground shadow-none ring-0 ring-offset-0 outline-none focus-visible:outline-none"
            : "h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
        )}

        style={
          isPlayer
            ? {
                width: "var(--player-progress-thumb-size, 0px)",
                height: "var(--player-progress-thumb-size, 0px)",
                opacity: "var(--player-progress-thumb-opacity, 0)",
                pointerEvents: "none",
              }
            : undefined
        }
      />
    </SliderPrimitive.Root>
  );
});
Slider.displayName = SliderPrimitive.Root.displayName;
export { Slider };
